import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import clsx from 'clsx';
import './card-glass-float.styles.scss';

const CardGlassFloat = ({
  children,
  className = '',
  image = null,
  imageAlt = '',
  title = '',
  subtitle = '',
  animationIntensity = 'medium', // 'subtle', 'medium', 'strong'
  glassIntensity = 'medium', // 'light', 'medium', 'strong'
  borderColor = 'teal', // 'teal', 'blue', 'purple', 'pink', 'custom'
  customBorderColor = null,
  onClick = null,
  initialRotation = { x: 8, y: -15 }, // Initial rotation angles
  ...props
}) => {
  const cardRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Start the floating animation after the initial load animation completes
    const timer = setTimeout(() => {
      setIsAnimating(true);
    }, 1400); // Match the duration of gentleScaleFadeIn animation

    return () => clearTimeout(timer);
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  // Memoize CSS classes to prevent unnecessary recalculations
  const cardClasses = useMemo(
    () =>
      clsx(
        'glass-float-card',
        'glass-float-card--floating',
        `glass-float-card--${glassIntensity}`,
        `glass-float-card--${animationIntensity}`,
        `glass-float-card--border-${borderColor}`,
        {
          'glass-float-card--hoverable': true,
          'glass-float-card--clickable': onClick,
          'glass-float-card--hovered': isHovered,
          'glass-float-card--initial-load': !isAnimating,
          'glass-float-card--animating': isAnimating
        },
        className
      ),
    [glassIntensity, animationIntensity, borderColor, onClick, isHovered, isAnimating, className]
  );

  // Memoize card style to prevent unnecessary recalculations
  const cardStyle = useMemo(
    () => ({
      '--initial-rotation-x': `${initialRotation.x}deg`,
      '--initial-rotation-y': `${initialRotation.y}deg`,
      ...(customBorderColor ? { '--custom-border-color': customBorderColor } : {})
    }),
    [initialRotation.x, initialRotation.y, customBorderColor]
  );

  return (
    <div
      ref={cardRef}
      className={cardClasses}
      style={cardStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      {...props}
    >
      {/* Animated background blur overlay */}
      <div className="glass-float-card__blur-overlay" />

      {/* Gradient border effect */}
      <div className="glass-float-card__border-gradient" />

      {/* Image container */}
      {image && (
        <div className="glass-float-card__image-container">
          <img src={image} alt={imageAlt} className="glass-float-card__image" loading="lazy" />
          <div className="glass-float-card__image-overlay" />
        </div>
      )}

      {/* Content - only render if there's actual content */}
      {(title || subtitle || children) && (
        <div className="glass-float-card__content">
          {title && <h3 className="glass-float-card__title">{title}</h3>}

          {subtitle && <p className="glass-float-card__subtitle">{subtitle}</p>}

          {children && <div className="glass-float-card__body">{children}</div>}
        </div>
      )}
    </div>
  );
};

export default CardGlassFloat;
